import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks';
import {
  clearOnboardingState,
  getStoredOnboardingState,
  hasHandledOnboardingCallback,
  markOnboardingCallbackHandled,
  parseOnboardingCallback,
} from '../../services/hostedOnboarding';
import { homePath, accountTypeFromRoles, rolesFromClaims } from '../../utils';
import { authStorage } from '../../services/authStorage';

const STATE_MISMATCH = 'Onboarding could not be completed. Please try again.';

export const OnboardingCallback: React.FC = () => {
  const { completeOnboardingHandoff } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const started = useRef(false);

  useEffect(() => {
    if (started.current || hasHandledOnboardingCallback()) return;
    started.current = true;

    const query = searchParams.toString();
    const href = query ? `/auth/callback?${query}` : window.location.href;
    const expectedState = getStoredOnboardingState();
    const callback = parseOnboardingCallback(href, expectedState);
    markOnboardingCallbackHandled();
    clearOnboardingState();

    if (callback.status === 'cancelled' || callback.status === 'ignored') {
      navigate('/login', { replace: true });
      return;
    }

    if (callback.status === 'state_mismatch' || callback.status === 'error' || !callback.code || !callback.state) {
      navigate('/login', {
        replace: true,
        state: { onboardingError: STATE_MISMATCH },
      });
      return;
    }

    void completeOnboardingHandoff({ code: callback.code, state: callback.state }).then(result => {
      if (result.outcome === 'authenticated') {
        const nextAccount =
          accountTypeFromRoles(rolesFromClaims(authStorage.getTokenClaims())) ?? 'employee';
        navigate(homePath(nextAccount), { replace: true });
        return;
      }

      if (result.outcome === 'pending') {
        navigate('/pending-approval', {
          replace: true,
          state: { message: result.message },
        });
        return;
      }

      navigate('/login', {
        replace: true,
        state: { onboardingError: result.error },
      });
    });
  }, [completeOnboardingHandoff, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600" />
    </div>
  );
};
